import type { In, Out } from "@clubcore/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMe } from "../auth";
import { useClub } from "../club";
import { useApi } from "../services";

export type DashboardSettings = In<"core_save_dashboard">["settings"];
export type DashboardSaved = Out<"core_read_dashboard">;
export type ChatUser = Out<"core_chat_inbox">["senders"][number];

export function useWorkspaceScope() {
  const api = useApi();
  const { club } = useClub();
  const me = useMe();
  const queryClient = useQueryClient();
  return { api, queryClient, key: [club.slug, me.user.id, "workspace"] as const };
}

export function useDashboardPreferences() {
  const { api, key } = useWorkspaceScope();
  return useQuery({ queryKey: [...key, "dashboard"], queryFn: ({ signal }) => api.workspace.dashboard(signal), staleTime: 60_000 });
}

export function useSaveDashboard() {
  const { api, key, queryClient } = useWorkspaceScope();
  return useMutation({ mutationFn: (body: In<"core_save_dashboard">) => api.workspace.saveDashboard(body), onSuccess: (data) => queryClient.setQueryData([...key, "dashboard"], data) });
}

export function useChatInbox() {
  const { api, key } = useWorkspaceScope();
  return useQuery({ queryKey: [...key, "chat-inbox"], queryFn: ({ signal }) => api.workspace.inbox(signal), staleTime: 10_000, refetchInterval: 15_000, refetchOnWindowFocus: "always" });
}
